'use client'

import * as Sentry from '@sentry/nextjs'
import { useEffect } from 'react'

/**
 * Last-resort error boundary, shared by both App Router roots.
 *
 * Next.js renders this in place of the root layout, so no stylesheet is
 * loaded — it must own its `<html>`/`<body>` and carry its own styling. It
 * previously used Tailwind token classes (`bg-background`, `bg-primary`);
 * those silently resolved to nothing here, and after the app/tenant root split
 * they would also have been the wrong identity: a customer-hostname request
 * can reach this file. Styling is inline and the copy is neutral for that
 * reason — no PixelMojo or Radar naming.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Log the error to Sentry
    Sentry.captureException(error)
  }, [error])

  return (
    <html lang='en'>
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem 1.25rem',
          background: '#0b0b0c',
          color: '#f4f4f5',
          fontFamily:
            'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          lineHeight: 1.5,
        }}
      >
        <main style={{ maxWidth: '34rem', textAlign: 'center' }}>
          <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
            Something went wrong
          </h1>
          <p style={{ margin: '0.75rem 0 1.5rem', color: '#a1a1aa' }}>
            The error has been reported.
          </p>
          <button
            type='button'
            onClick={() => reset()}
            style={{
              border: '1px solid #52525b',
              background: 'transparent',
              color: 'inherit',
              font: 'inherit',
              padding: '0.625rem 1.25rem',
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </main>
      </body>
    </html>
  )
}
